/**
 * Il risultato di un esercizio di tiro, letto: hit factor, punti, serie e
 * dettaglio bersaglio per bersaglio.
 *
 * Viveva dentro `ExerciseDetailAccordion` e serve anche allo **storico**
 * dell'esercizio, che prima mostrava una riga sola («HF 4.25 · 4 serie») e
 * nessun modo di vedere dove si erano persi i punti. Un modulo suo, così lo
 * leggono tutti e due senza import circolari (l'accordion importa la
 * Dashboard, che importa lo storico).
 *
 * Spec: docs/shooting/17-terza-sessione-federico.md
 */
import { hasContent } from "../../lib/conditioningResultUtils";
import { scorableTargets, targetLabelOf, targetLabels } from "./TargetScoreCard";
import { isSteelTarget } from "./stageElements";
import { seriesDescription } from "./shootingPlan";

// I tempi di tiro sono secondi coi centesimi (4.82s), non MM:SS: fra 4.82 e 4.89
// c'è la differenza fra due piazzamenti, quindi niente arrotondamento al secondo.
const formatShotTime = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n) || n < 0) return null;
	return `${n.toFixed(2)}s`;
};

const formatHitFactor = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return null;
	return n.toFixed(2);
};

const formatPoints = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return null;
	return Number.isInteger(n) ? String(n) : n.toFixed(1);
};

const formatPct = (value) => {
	const n = Number(value);
	if (!Number.isFinite(n)) return null;
	return `${Math.round(n)}%`;
};

// { A: 6, C: 1 } → "A6 · C1"; scarta i codici a zero.
const formatZoneCounts = (counts) => {
	if (!counts || typeof counts !== "object") return "";
	return Object.entries(counts)
		.filter(([, count]) => Number(count) > 0)
		.map(([code, count]) => `${code}${count}`)
		.join(" · ");
};

// "production_optics" → "Production optics", come chip di contesto leggibile.
const humanizeToken = (value) => {
	const s = String(value || "").trim();
	if (!s) return "";
	const spaced = s.replace(/_/g, " ");
	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Risultato di tiro (dry fire / live fire). I valori veri vivono in
 * `result.shooting` (hit factor, punti, stringhe) — ricalcolati server-side —
 * non nei campi kg/reps/tempo dello strength, quindi serve un blocco dedicato
 * o il coach vede una riga vuota.
 */
export function ShootingResultBlock({ exercise, result, isCoachView = false }) {
	const shooting =
		result?.shooting && typeof result.shooting === "object"
			? result.shooting
			: null;
	const mode =
		shooting?.mode ||
		(exercise?.exercise_type === "dry_fire" ? "dry_fire" : "live_fire");
	const isFree = shooting?.prescription_format === "free";
	const modeLabel = mode === "dry_fire" ? "Dry fire" : "Live fire";

	// Prescritto vs eseguito. **Non c'era da nessuna parte**: il coach vedeva
	// il risultato senza sapere se bastava. Un hit factor di 4.2 non dice
	// niente finché non sai che l'obiettivo era 5.0, e 38 colpi su 36 previsti
	// e' un dato di allestimento, non un dettaglio.
	const prescribed = exercise?.shooting || {};
	// `rounds_planned` sono i colpi **per serie**; `rounds_fired` e' il totale
	// sparato. Il confronto va fatto sul totale previsto (colpi × serie), o
	// quattro serie da 12 colpi uscivano come «48 / 12» in ambra.
	const seriesDone = (shooting?.series || shooting?.strings || []).length;
	const seriesPlanned =
		(Array.isArray(prescribed.series) && prescribed.series.length) ||
		prescribed.series_planned ||
		prescribed.strings_planned ||
		0;
	const roundsExpected = prescribed.rounds_planned
		? prescribed.rounds_planned * Math.max(1, seriesPlanned)
		: 0;
	const comparisons = [
		roundsExpected > 0 && [
			"Colpi",
			`${shooting?.rounds_fired ?? "—"} / ${roundsExpected}`,
			shooting?.rounds_fired != null && shooting.rounds_fired !== roundsExpected,
		],
		seriesPlanned > 0 && [
			"Serie",
			`${seriesDone} / ${seriesPlanned}`,
			seriesDone < seriesPlanned,
		],
		prescribed.par_time_seconds &&
			shooting?.best_time_seconds != null && [
				"Par",
				`${formatShotTime(shooting.best_time_seconds)} / ${formatShotTime(prescribed.par_time_seconds)}`,
				shooting.best_time_seconds > prescribed.par_time_seconds,
			],
	].filter(Boolean);
	const goal = prescribed.athlete_goal || prescribed.success_criteria;

	// Contesto congelato al salvataggio: division, power factor, classe.
	const contextChips = [
		humanizeToken(shooting?.division),
		shooting?.power_factor
			? shooting.power_factor === "major"
				? "Major"
				: "Minor"
			: "",
		shooting?.class_at_time ? String(shooting.class_at_time).toUpperCase() : "",
	].filter(Boolean);

	// `series` è il nome attuale, `strings` quello dei documenti più vecchi.
	const strings = Array.isArray(shooting?.series)
		? shooting.series
		: Array.isArray(shooting?.strings)
			? shooting.strings
			: [];
	const times = Array.isArray(shooting?.times)
		? shooting.times.map((t) => formatShotTime(t)).filter(Boolean)
		: [];

	// Il numero in evidenza è quello della **metrica del sistema**, congelata
	// sul risultato: con un sistema a soli punti l'hit factor è zero e
	// «HF 0.00» in testa diceva il contrario di com'era andata.
	const metric = String(shooting?.metric || "hit_factor").toLowerCase();
	const severalSeries =
		strings.length > 1 && shooting?.series_scoring !== "combined";
	const bestIndex = severalSeries ? shooting?.best_series_index : null;
	const headline =
		metric === "time_plus"
			? formatShotTime(shooting?.best_score ?? shooting?.total_final_time_seconds)
			: metric === "points"
				? formatPoints(shooting?.best_score ?? shooting?.total_points) &&
					`${formatPoints(shooting?.best_score ?? shooting?.total_points)} punti`
				: formatHitFactor(shooting?.hit_factor) &&
					`HF ${formatHitFactor(shooting?.hit_factor)}`;
	// Le zone di ogni bersaglio vengono dalla prescrizione: è lì che si sa
	// quale T è una carta e quale P un popper.
	const prescribedTargets = scorableTargets(prescribed.targets);
	const labels = targetLabels(prescribed.targets);
	const steelNos = new Set(
		prescribedTargets
			.filter((target) => isSteelTarget(target.target_type))
			.map((target) => target.target_no),
	);
	const hf = formatHitFactor(shooting?.hit_factor);
	const totalPoints = formatPoints(shooting?.total_points);
	const totalTime = formatShotTime(shooting?.total_time_seconds);
	const accuracy = formatPct(shooting?.accuracy_pct);
	const hitRate = formatPct(shooting?.hit_rate_pct);
	const bestTime = formatShotTime(shooting?.best_time_seconds);
	const avgTime = formatShotTime(shooting?.avg_time_seconds);
	const successRate = formatPct(shooting?.success_rate_pct);
	const selfRating = Number(shooting?.self_rating) || null;

	const hasAnyData =
		!!hf ||
		!!totalPoints ||
		!!totalTime ||
		!!bestTime ||
		!!avgTime ||
		times.length > 0 ||
		strings.length > 0 ||
		!!selfRating ||
		hasContent(result?.notes);

	return (
		<div className="space-y-1.5 text-xs">
			<div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
				<span className="font-medium text-foreground">{modeLabel}</span>
				{contextChips.map((chip, i) => (
					<span
						key={i}
						className="text-[10px] uppercase tracking-wider text-muted-foreground"
					>
						{chip}
					</span>
				))}
			</div>

			{/* Prescritto vs eseguito, solo lato coach: nel diario l'atleta ha
			    già la prescrizione sopra, e ripeterla è rumore. */}
			{isCoachView && (comparisons.length > 0 || goal) && (
				<div className="flex flex-wrap items-center gap-x-3 gap-y-1 pb-0.5">
					{comparisons.map(([label, value, off]) => (
						<span key={label} className="text-[11px]">
							<span className="text-muted-foreground">{label} </span>
							<span className={off ? "text-amber-400" : "text-foreground"}>
								{value}
							</span>
						</span>
					))}
					{goal && (
						<span className="text-[11px]">
							<span className="text-muted-foreground">Obiettivo </span>
							<span className="text-foreground">{goal}</span>
						</span>
					)}
				</div>
			)}

			{/* Formato libero: non c'è punteggio, solo l'autovalutazione. */}
			{isFree ? (
				selfRating ? (
					<div>
						<span className="text-muted-foreground">Autovalutazione: </span>
						<span className="text-volt font-medium">{selfRating}/5</span>
					</div>
				) : null
			) : mode === "dry_fire" ? (
				<>
					{bestTime && (
						<div className="text-volt">
							<span className="text-muted-foreground">Miglior tempo: </span>
							<span className="font-medium">{bestTime}</span>
						</div>
					)}
					{avgTime && (
						<div>
							<span className="text-muted-foreground">Media: </span>
							{avgTime}
						</div>
					)}
					{shooting?.par_time_seconds ? (
						<div>
							<span className="text-muted-foreground">Par: </span>
							{formatShotTime(shooting.par_time_seconds)}
							{successRate ? ` · ${successRate} dentro il par` : ""}
						</div>
					) : successRate ? (
						<div>
							<span className="text-muted-foreground">Dentro il par: </span>
							{successRate}
						</div>
					) : null}
					{times.length > 0 && (
						<div>
							<span className="text-muted-foreground">Tempi: </span>
							{times.join(" · ")}
						</div>
					)}
					{selfRating && (
						<div>
							<span className="text-muted-foreground">Autovalutazione: </span>
							{selfRating}/5
						</div>
					)}
				</>
			) : (
				<>
					{/* Live fire: il numero della metrica e i punti in evidenza. */}
					{(headline || totalPoints || totalTime) && (
						<div className="flex flex-wrap items-baseline gap-x-2">
							{headline && (
								<span className="text-volt font-semibold">
									{headline}
									{severalSeries && (
										<span className="text-[10px] font-normal text-muted-foreground uppercase tracking-wider">
											{" "}
											serie migliore
										</span>
									)}
								</span>
							)}
							{totalPoints && (
								<span className="text-foreground">
									<span className="text-muted-foreground">· </span>
									{totalPoints} punti{severalSeries ? " in totale" : ""}
								</span>
							)}
							{totalTime && (
								<span className="text-muted-foreground">· {totalTime}</span>
							)}
						</div>
					)}
					{(accuracy || hitRate || shooting?.rounds_fired) && (
						<div className="text-muted-foreground">
							{accuracy ? `${accuracy} accuratezza` : ""}
							{accuracy && (hitRate || shooting?.rounds_fired) ? " · " : ""}
							{hitRate ? `${hitRate} a bersaglio` : ""}
							{hitRate && shooting?.rounds_fired ? " · " : ""}
							{shooting?.rounds_fired ? `${shooting.rounds_fired} colpi` : ""}
						</div>
					)}
					{strings.length > 0 && (
						<div className="space-y-1 pt-1">
							{strings.map((str, j) => {
								const strTime = formatShotTime(str?.time_seconds);
								const strHf =
									metric === "hit_factor" ? formatHitFactor(str?.hit_factor) : null;
								const strFinal =
									metric === "time_plus"
										? formatShotTime(str?.final_time_seconds)
										: null;
								const isBest = bestIndex === j;
								const description = seriesDescription(str);
								const strPoints = formatPoints(str?.points);
								const hits = formatZoneCounts(str?.hits);
								const penalties = formatZoneCounts(str?.penalties);
								return (
									<div
										key={str?.string_index ?? j}
										className={`flex flex-wrap items-baseline gap-x-2 rounded-sm px-2 py-1.5 ${
											isBest
												? "bg-volt/10 border border-volt/30"
												: "bg-secondary/40"
										}`}
									>
										<span className="text-muted-foreground shrink-0">
											Serie {str?.series_index ?? str?.string_index ?? j + 1}
										</span>
										{/* Distanza ed etichetta: è ciò che rende il punteggio
										    confrontabile fra una serie e l'altra (10 m, «da
										    sinistra a destra»). */}
										{description && (
											<span className="text-foreground font-medium">
												{description}
											</span>
										)}
										{isBest && (
											<span className="text-[10px] uppercase tracking-wider text-volt">
												migliore
											</span>
										)}
										{strTime && (
											<span className="text-foreground">{strTime}</span>
										)}
										{hits && (
											<span className="text-muted-foreground">· {hits}</span>
										)}
										{penalties && (
											<span className="text-red-400/90">· {penalties}</span>
										)}
										{strPoints && (
											<span className="text-muted-foreground">
												· {strPoints} pt
											</span>
										)}
										{strHf && <span className="text-volt">· HF {strHf}</span>}
										{strFinal && (
											<span className="text-volt">· {strFinal} finale</span>
										)}
										{/* Dove si sono persi i punti. "Due Charlie su T4"
										    è un'indicazione di allenamento; "due Charlie"
										    non lo è. */}
										{(str?.targets || []).length > 0 && (
											<span className="basis-full text-[11px] text-muted-foreground pt-0.5">
												{[...str.targets]
													.sort((a, b) => (a?.target_no || 0) - (b?.target_no || 0))
													.map((target) => {
														// Il ferro non ha zone: si dice se è
														// caduto, non «A1».
														const hitCount = Object.values(target?.hits || {}).reduce(
															(sum, n) => sum + (Number(n) || 0),
															0,
														);
														const detail = [
															steelNos.has(target?.target_no)
																? hitCount > 0
																	? "abbattuto"
																	: ""
																: formatZoneCounts(target?.hits),
															formatZoneCounts(target?.penalties),
														]
															.filter(Boolean)
															.join(" ");
														return `${targetLabelOf(labels, target?.target_no)} ${detail || "—"}`;
													})
													.join("  ·  ")}
											</span>
										)}
									</div>
								);
							})}
						</div>
					)}
				</>
			)}

			{hasContent(result?.notes) && (
				<div className="pt-1 italic text-muted-foreground">
					"{result.notes}"
				</div>
			)}

			{!hasAnyData && (
				<div className="text-muted-foreground/60 italic">
					Nessun dettaglio di tiro registrato.
				</div>
			)}
		</div>
	);
}
